import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LightboxPreview, type ScanItem } from '../components/LightboxPreview';
import { FormatDialog } from '../components/FormatDialog';
import { DownloadPanel } from '../components/DownloadPanel';
import { OrderTrackingSummary } from '../components/OrderTrackingSummary';
import { RollProgress } from '../components/RollProgress';
import { buildRollSections, downloadRequestFor, flattenScansForLightbox } from '../utils/rollUtils';
import { useFeature, SCAN_EDITING, PRINT_ORDERING } from '../contexts/ConfigContext';
import { usePrintSelection, type ScanMeta } from '../contexts/PrintSelectionContext';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useConfirm } from '../hooks/useConfirm';
import { useDownloadJob } from '../hooks/useDownloadJob';
import { useCollapsedRolls } from '../hooks/useCollapsedRolls';
import { discountPercent, estimatedTotal, gbp, usePrintPricing } from '../hooks/usePrintPricing';
import { gallery } from '../api/gallery';
import { isApiError } from '../api/client';
import { queryKeys } from '../api/queryKeys';
import type { DownloadFormat, RollSection, Scan, ScansResponse } from '../types/roll';
import { rollStatus } from '../utils/orderTracking';
import styles from './GalleryPage.module.css';

/** A scan can be selected, to download or to print, once its image is ready. */
function isSelectable(scan: Scan): boolean {
  return scan.thumbnail_ready && scan.edit_status !== 'failed';
}

/** True while a scan is still being generated (and hasn't failed). */
function isProcessing(scan: Scan): boolean {
  return !scan.thumbnail_ready && scan.edit_status !== 'failed';
}

async function fetchScans(orderId: string, preview: boolean, handleUnauthorized: () => void): Promise<ScansResponse> {
  if (!preview) return gallery.scans(orderId);
  // An admin previews through an admin-authorised endpoint that returns the
  // customer's payload, so the page renders exactly what the customer sees.
  try {
    return await gallery.previewScans(orderId);
  } catch (err) {
    // Only preview sits behind the admin session; the customer path has its
    // own auth context and must not clear the admin flag on its 401s.
    if (isApiError(err) && err.unauthorized) handleUnauthorized();
    throw err;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** What a roll without scans says: why there are none, or when they'll come. */
function emptyRollText(section: RollSection): string {
  if (section.is_empty) return 'No images could be recovered from this roll.';
  if (section.tracking?.kind === 'develop_only') return "You didn't order scans for this roll.";
  if (section.tracking) return "Your scans will appear here when they're ready.";
  return 'No scans available in this roll';
}

/** Number of images in the first row that get eager loading (desktop 4-column grid). */
const EAGER_LOAD_COUNT = 4;

/** Poll interval while any scan (e.g. a rendering edit) is still processing. */
const PROCESSING_POLL_MS = 3000;

export function GalleryPage({ preview = false }: { preview?: boolean } = {}) {
  const { orderId } = useParams<{ orderId: string }>();
  if (!orderId) return null;
  // Keyed so moving to another order starts it with nothing selected,
  // collapsed or open, rather than carrying the last order's state over.
  return <OrderGallery key={orderId} orderId={orderId} preview={preview} />;
}

function OrderGallery({ orderId, preview }: { orderId: string; preview: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editingEnabled = useFeature(SCAN_EDITING) && !preview;
  const printEnabled = useFeature(PRINT_ORDERING) && !preview;
  // Preview is read-only: an admin sees the gallery but can't act on it.
  const selectionEnabled = !preview;
  const printSelection = usePrintSelection();
  const basketCount = printSelection.lines.length;
  // Prices the dock's running total; nothing to price until the basket has prints.
  const { data: pricing } = usePrintPricing(printEnabled && basketCount > 0);
  const { handleUnauthorized } = useAdminAuth();
  const { confirm, confirmDialog } = useConfirm();
  const download = useDownloadJob();
  const { collapsed, toggle: toggleRoll, collapseAll, expandAll } = useCollapsedRolls(orderId);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [deletingScanIds, setDeletingScanIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [contactSheetRollId, setContactSheetRollId] = useState<string | null>(null);
  const [contactSheetError, setContactSheetError] = useState<{ rollId: string; message: string } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [formatTarget, setFormatTarget] = useState<'all' | 'selected' | null>(null);
  // How many images the last "Add to prints" added, while its note is up.
  const [addedToPrints, setAddedToPrints] = useState<number | null>(null);

  const { data, isLoading, isError, error } = useQuery<ScansResponse>({
    // Keep the preview payload cache-separate from the customer one.
    queryKey: queryKeys.scansView(orderId, preview),
    queryFn: () => fetchScans(orderId, preview, handleUnauthorized),
    // While anything is still rendering (a saved edit, a fresh thumbnail),
    // poll until every scan is ready or failed.
    refetchInterval: (query) => {
      const rolls = query.state.data?.rolls;
      const anyProcessing = rolls?.some((roll) => roll.scans.some(isProcessing));
      return anyProcessing ? PROCESSING_POLL_MS : false;
    },
  });

  usePageTitle(preview ? 'Admin — preview' : data?.order_number ?? 'Gallery');

  const rollSections = useMemo(() => (data?.rolls ? buildRollSections(data.rolls) : []), [data?.rolls]);
  const flatScans = useMemo(() => (data?.rolls ? flattenScansForLightbox(data.rolls) : []), [data?.rolls]);
  const scanIndex = useMemo(() => new Map(flatScans.map((scan, i) => [scan.scan_id, i])), [flatScans]);

  // The selection, less anything that has stopped being selectable since it
  // was ticked (an edit deleted, say).
  const selectedIds = useMemo(() => {
    const selectable = new Set(flatScans.filter(isSelectable).map((s) => s.scan_id));
    return new Set([...selected].filter((id) => selectable.has(id)));
  }, [selected, flatScans]);
  const selectedCount = selectedIds.size;

  /** Print ScanMeta for one selectable scan. */
  const scanMeta = useCallback(
    (scan: Scan, rollId: string): ScanMeta => ({
      scan_id: scan.scan_id,
      filename: scan.original_filename,
      thumbnail_url: scan.thumbnail_url,
      roll_id: rollId,
      // Lets the basket offer a way back to this gallery. Carried on the line
      // itself rather than in router state, which is lost the moment the page
      // reloads and absent entirely when /print is reached from the header nav.
      order_id: orderId,
    }),
    [orderId],
  );

  const printMetas = useMemo(
    () => rollSections.flatMap((section) => section.scans.filter(isSelectable).map((s) => scanMeta(s, section.roll_id))),
    [rollSections, scanMeta],
  );

  // Enrich any selection restored from the server (which persists only
  // scan_id/size/finish/quantity) with fresh filenames + thumbnail URLs.
  useEffect(() => {
    if (printEnabled && printMetas.length > 0) {
      printSelection.hydrateFromScans(printMetas);
    }
    // printSelection identity changes each render; depend only on the data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printMetas, printEnabled]);

  const toggleScan = useCallback((scanId: string) => {
    setAddedToPrints(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(scanId)) next.add(scanId);
      return next;
    });
  }, []);

  const setScansSelected = useCallback((scanIds: string[], on: boolean) => {
    setAddedToPrints(null);
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of scanIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const addToPrints = useCallback(() => {
    const metas = printMetas.filter((m) => selectedIds.has(m.scan_id));
    // Adds only what isn't in the basket already; sizes are chosen there.
    printSelection.selectRoll(metas);
    setAddedToPrints(metas.length);
    setSelected(new Set());
  }, [printMetas, selectedIds, printSelection]);

  const startDownload = useCallback(
    (format: DownloadFormat) => {
      if (!data) return;
      const scanIds = formatTarget === 'all' ? new Set(flatScans.map((s) => s.scan_id)) : selectedIds;
      setFormatTarget(null);
      void download.start(downloadRequestFor(scanIds, data.rolls, format));
    },
    [data, formatTarget, flatScans, selectedIds, download],
  );

  const handleEditScan = useCallback(
    (scan: ScanItem) => {
      setLightboxIndex(null);
      navigate(`/orders/${orderId}/edit/${scan.scan_id}`);
    },
    [navigate, orderId],
  );

  const handleDeleteEdit = useCallback(
    async (scan: Scan) => {
      const confirmed = await confirm({
        title: 'Delete edit',
        message: 'Delete this edited image? This cannot be undone — the original scan is unaffected.',
        confirmLabel: 'Delete',
        destructive: true,
      });
      if (!confirmed) return;
      setDeleteError(null);
      setDeletingScanIds((prev) => new Set(prev).add(scan.scan_id));
      try {
        try {
          await gallery.deleteEdit(orderId, scan.scan_id);
        } catch (err) {
          // Already gone is as good as deleted.
          if (!isApiError(err, 404)) throw err;
        }
        await queryClient.invalidateQueries({ queryKey: queryKeys.scans(orderId) });
      } catch {
        setDeleteError('Could not delete the edit. Please try again.');
      } finally {
        setDeletingScanIds((prev) => {
          const next = new Set(prev);
          next.delete(scan.scan_id);
          return next;
        });
      }
    },
    [orderId, queryClient, confirm],
  );

  const handleContactSheet = useCallback(
    async (rollId: string) => {
      setContactSheetError(null);
      setContactSheetRollId(rollId);
      try {
        const { url, filename } = await gallery.contactSheet(orderId, rollId);
        // The presigned URL carries a Content-Disposition attachment header, so
        // a plain anchor click downloads the PDF without navigating away.
        const link = document.createElement('a');
        link.href = url;
        if (filename) link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        setContactSheetError({
          rollId,
          message:
            (isApiError(err) && err.serverMessage) || 'Could not generate the contact sheet. Please try again.',
        });
      } finally {
        setContactSheetRollId((current) => (current === rollId ? null : current));
      }
    },
    [orderId],
  );

  const handleImageError = useCallback((scanId: string) => {
    setFailedImages((prev) => new Set(prev).add(scanId));
  }, []);

  // In preview, an admin returns to the order's admin page; otherwise the
  // customer returns to their order list.
  const backTo = preview ? `/admin/orders/${orderId}/upload` : '/orders';
  const backLabel = preview ? '← Back to order' : '← Back to orders';
  const displayOrderName = data?.order_number ?? `Order ${orderId}`;
  const header = (
    <>
      <Link to={backTo} className={styles.backLink}>
        {backLabel}
      </Link>
      <h1 className={styles.heading}>{displayOrderName}</h1>
    </>
  );

  if (isLoading) {
    return (
      <main className={styles.page}>
        {header}
        <p className={styles.loading}>Loading scans...</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className={styles.page}>
        {header}
        <div className={styles.fetchError} role="alert">
          <p>
            {isApiError(error, 404)
              ? "We couldn't find this order."
              : "We couldn't load this order's scans. Please try again."}
          </p>
        </div>
      </main>
    );
  }

  if (data?.expired) {
    return (
      <main className={styles.page}>
        {header}
        <div className={styles.expiredMessage} role="alert">
          <p>
            The scans for this order are no longer available.
            {data.expired_at && <> The retention period expired on {formatDate(data.expired_at)}.</>}
          </p>
        </div>
      </main>
    );
  }

  // Count originals only, so the gallery total matches the order list's stable
  // count. `data.scan_count` reflects all current scans including the customer's
  // "My edits" copies, which inflates the figure as edits are saved.
  const scanCount = rollSections
    .filter((section) => section.roll_type !== 'edits')
    .reduce((sum, section) => sum + section.scans.length, 0);
  // Edits have no TIFF master; the format dialog says what they come as when
  // the download includes any.
  const editIds = new Set(
    rollSections.flatMap((r) => r.scans.filter((s) => r.roll_type === 'edits' || s.source_scan_id)).map((s) => s.scan_id),
  );
  const downloadHasEdits =
    formatTarget === 'selected' ? [...selectedIds].some((id) => editIds.has(id)) : editIds.size > 0;
  const fromGallery = { fromGallery: `/orders/${orderId}` };
  const allCollapsed = rollSections.length > 0 && rollSections.every((s) => collapsed.has(s.roll_id));
  // The basket is shared by every order, so the prints bar totals all of it,
  // priced as the basket page prices it: each line at its own size, with the
  // volume discount the whole basket earns.
  const showPrintsBar = printEnabled && basketCount > 0;
  const basketDiscount = discountPercent(printSelection.totalQuantity, pricing?.volume_discounts);
  const basketTotal = estimatedTotal(printSelection.lines, pricing, basketDiscount);
  const showDock = selectedCount > 0 || showPrintsBar || download.preparing || !!download.job || !!download.error;
  const announcement =
    selectedCount > 0
      ? `${selectedCount} selected`
      : addedToPrints !== null
        ? `Added ${plural(addedToPrints, 'image')} to your prints`
        : '';

  return (
    <main className={styles.page}>
      {header}
      {/* With tracking, an order still in the lab has its progress to show
          instead of "0 scans". */}
      {(scanCount > 0 || !data?.tracking) && <p className={styles.scanCount}>{plural(scanCount, 'scan')}</p>}

      {preview && (
        <div className={styles.previewBanner} role="status">
          <strong>Preview as customer.</strong> This is what the customer sees, with order tracking on. Download and
          other actions are disabled here.
        </div>
      )}

      {data?.tracking && <OrderTrackingSummary tracking={data.tracking} rolls={rollSections} />}

      {flatScans.length > 0 && (
        <p className={styles.intro}>
          Here are the scans from your order, grouped by roll below.
          {editingEnabled && (
            <>
              {' '}
              Tap any scan to view it full-screen, then choose <strong>Edit photo</strong> to crop, add a border, or
              adjust exposure, contrast and colour. Your edited copies are saved to a new <strong>My edits</strong>{' '}
              roll below.
            </>
          )}
          {selectionEnabled &&
            (printEnabled
              ? ' Tick the images you want to download or have printed, or download the whole order at once.'
              : ' Tick the images you want to download, or download the whole order at once.')}
        </p>
      )}

      {/* An order whose rolls all came back empty has no scans to introduce and
          nothing to download, so the usual intro and actions are both absent.
          Say what happened instead of leaving a bare heading over "0 scans". */}
      {flatScans.length === 0 && rollSections.some((section) => section.is_empty) && (
        <p className={styles.intro}>
          Your film was developed, but there were no images we could scan. Where we've added a note, it's shown
          with the roll below.
        </p>
      )}

      {flatScans.length > 0 && (
        <div className={styles.actions}>
          {preview ? (
            <span className={styles.downloadDisabled} aria-disabled="true">
              Download scans (disabled in preview)
            </span>
          ) : (
            <button
              type="button"
              className={styles.downloadButton}
              onClick={() => setFormatTarget('all')}
              disabled={download.preparing}
              aria-label="Download all scans"
            >
              Download all
            </button>
          )}
          {rollSections.length > 1 && (
            <div className={styles.rollToggles}>
              <button type="button" className={styles.textButton} onClick={expandAll} disabled={collapsed.size === 0}>
                Expand all
              </button>
              <button
                type="button"
                className={styles.textButton}
                onClick={() => collapseAll(rollSections.map((s) => s.roll_id))}
                disabled={allCollapsed}
              >
                Collapse all
              </button>
            </div>
          )}
        </div>
      )}

      {rollSections.length === 0 ? (
        // Before the film arrives, the summary above says what's coming.
        data?.tracking?.phase !== 'awaiting_film' && <p className={styles.emptyState}>No scans available for this order</p>
      ) : (
        rollSections.map((section) => {
          const isEditsRoll = section.roll_type === 'edits';
          const isCollapsed = collapsed.has(section.roll_id);
          const bodyId = `roll-${section.roll_id}`;
          const generatingSheet = contactSheetRollId === section.roll_id;
          const sheetError = contactSheetError?.rollId === section.roll_id ? contactSheetError.message : null;
          const selectableInRoll = selectionEnabled
            ? section.scans.filter(isSelectable).map((s) => s.scan_id)
            : [];
          const selectedInRoll = selectableInRoll.filter((id) => selectedIds.has(id)).length;
          const wholeRollSelected = selectableInRoll.length > 0 && selectedInRoll === selectableInRoll.length;
          return (
            <section key={section.roll_id} className={styles.rollSection}>
              <div className={styles.rollHeader}>
                <h2 className={styles.rollHeading}>
                  <button
                    type="button"
                    className={styles.rollToggle}
                    aria-expanded={!isCollapsed}
                    aria-controls={bodyId}
                    onClick={() => toggleRoll(section.roll_id)}
                  >
                    <svg className={styles.chevron} viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <polyline points="9 6 15 12 9 18" fill="none" stroke="currentColor" strokeWidth="2.5" />
                    </svg>
                    <span>{section.roll_name}</span>
                    {(section.scans.length > 0 || !section.tracking) && (
                      <span className={styles.rollCount}>{plural(section.scans.length, 'scan')}</span>
                    )}
                  </button>
                </h2>
                {/* Outside the heading, so the roll's name stays its name. */}
                {section.tracking && <p className={styles.rollStatus}>{rollStatus(section.tracking)}</p>}
                <div className={styles.rollActions}>
                  {selectableInRoll.length > 0 && (
                    <label className={styles.rollSelectLabel}>
                      <input
                        type="checkbox"
                        className={styles.rollSelect}
                        checked={wholeRollSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = selectedInRoll > 0 && !wholeRollSelected;
                        }}
                        onChange={(e) => setScansSelected(selectableInRoll, e.target.checked)}
                        aria-label={`Select all scans in ${section.roll_name}`}
                      />
                      Select roll
                    </label>
                  )}
                  {!preview && section.scans.length > 0 && (
                    <button
                      type="button"
                      className={styles.contactSheetButton}
                      onClick={() => handleContactSheet(section.roll_id)}
                      disabled={generatingSheet}
                      aria-label={`Download a contact sheet PDF for ${section.roll_name}`}
                    >
                      {generatingSheet ? 'Generating…' : 'Contact sheet'}
                    </button>
                  )}
                </div>
              </div>
              {sheetError && (
                <p className={styles.contactSheetError} role="alert">
                  {sheetError}
                </p>
              )}

              {/* A collapsed roll renders nothing inside, so a long order's
                  hidden rolls don't load their thumbnails. */}
              <div id={bodyId} hidden={isCollapsed}>
                {!isCollapsed && (
                  <>
                    {section.tracking && <RollProgress rollName={section.roll_name} tracking={section.tracking} />}

                    {section.lab_notes && (
                      <aside className={styles.labNotes} aria-label={`Lab notes for ${section.roll_name}`}>
                        <span className={styles.labNotesLabel}>Note from the lab</span>
                        <p className={styles.labNotesText}>{section.lab_notes}</p>
                      </aside>
                    )}

                    {section.scans.length === 0 ? (
                      <p className={styles.rollEmptyState}>{emptyRollText(section)}</p>
                    ) : (
                      <div className={`${styles.grid}${selectedCount > 0 ? ` ${styles.selecting}` : ''}`}>
                        {section.scans.map((scan) => {
                          const index = scanIndex.get(scan.scan_id) ?? 0;
                          const selectable = selectionEnabled && isSelectable(scan);
                          const isSelected = selectable && selectedIds.has(scan.scan_id);
                          const inPrints = printEnabled && printSelection.isScanSelected(scan.scan_id);
                          const deletable = isEditsRoll && editingEnabled;
                          const deleting = deletingScanIds.has(scan.scan_id);

                          let tileContent;
                          if (scan.edit_status === 'failed') {
                            tileContent = <div className={styles.errorPlaceholder}>Edit failed</div>;
                          } else if (!scan.thumbnail_ready) {
                            tileContent = (
                              <div
                                className={styles.processingPlaceholder}
                                role="status"
                                aria-label={`Processing ${scan.original_filename}`}
                              >
                                Processing…
                              </div>
                            );
                          } else if (failedImages.has(scan.scan_id)) {
                            tileContent = <div className={styles.errorPlaceholder}>Failed to load</div>;
                          } else {
                            tileContent = (
                              <img
                                src={scan.thumbnail_url}
                                alt={scan.original_filename}
                                className={styles.thumbnailImage}
                                loading={index < EAGER_LOAD_COUNT ? 'eager' : 'lazy'}
                                onError={() => handleImageError(scan.scan_id)}
                              />
                            );
                          }

                          return (
                            <div
                              key={scan.scan_id}
                              className={`${styles.tile}${isSelected ? ` ${styles.tileSelected}` : ''}`}
                            >
                              {selectable && (
                                <label className={styles.tileCheck}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleScan(scan.scan_id)}
                                    aria-label={`Select ${scan.original_filename}`}
                                  />
                                </label>
                              )}
                              <button
                                type="button"
                                className={styles.thumbnailButton}
                                data-scan-index={index}
                                aria-label={`View scan ${scan.original_filename}`}
                                onClick={() => setLightboxIndex(index)}
                                disabled={isProcessing(scan)}
                              >
                                {tileContent}
                              </button>
                              {inPrints && <span className={styles.inPrints}>In prints</span>}
                              {deletable && (
                                <button
                                  type="button"
                                  className={styles.deleteEditButton}
                                  aria-label={`Delete edit ${scan.original_filename}`}
                                  onClick={() => handleDeleteEdit(scan)}
                                  disabled={deleting}
                                >
                                  {deleting ? '…' : 'Delete'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          );
        })
      )}

      {deleteError && (
        <p className={styles.fetchError} role="alert">
          {deleteError}
        </p>
      )}

      <p className={styles.visuallyHidden} aria-live="polite">
        {announcement}
      </p>

      {showDock && (
        <div className={styles.dock} data-dock>
          <DownloadPanel download={download} />
          {selectedCount > 0 && (
            <div className={styles.selectionBar}>
              <span className={styles.selectionCount}>{selectedCount} selected</span>
              <div className={styles.selectionActions}>
                <button
                  type="button"
                  className={styles.barPrimary}
                  onClick={() => setFormatTarget('selected')}
                  disabled={download.preparing}
                  aria-label={`Download ${plural(selectedCount, 'selected scan')}`}
                >
                  Download
                </button>
                {printEnabled && (
                  <button type="button" className={styles.barSecondary} onClick={addToPrints}>
                    Add to prints
                  </button>
                )}
                <button type="button" className={styles.barText} onClick={clearSelection}>
                  Clear
                </button>
              </div>
            </div>
          )}
          {showPrintsBar && (
            <section className={styles.printsBar} aria-label="Your prints">
              <div className={styles.printsInfo}>
                <span className={styles.printsHeading}>
                  {addedToPrints !== null ? (
                    <>
                      <span aria-hidden="true">✓ </span>Added {plural(addedToPrints, 'image')} to your prints
                    </>
                  ) : (
                    'Your prints'
                  )}
                </span>
                <span className={styles.printsSummary}>
                  <span className={styles.printsCounts}>
                    {plural(basketCount, 'image')} · {plural(printSelection.totalQuantity, 'print')}
                  </span>
                  {basketTotal != null && (
                    <span>
                      <span className={styles.printsCounts}>{' · '}</span>
                      <strong className={styles.printsTotal}>{gbp(basketTotal)}</strong> estimated
                      {basketDiscount > 0 && ` (${basketDiscount}% off)`}
                    </span>
                  )}
                </span>
              </div>
              {/* The gallery we came from rides along so the basket can link
                  back to it; router state doesn't survive a full page load,
                  so the basket treats it as a nicety. */}
              <Link to="/print" state={fromGallery} className={styles.printsLink}>
                Review prints
              </Link>
            </section>
          )}
        </div>
      )}

      {lightboxIndex !== null && (
        <LightboxPreview
          scans={flatScans}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onEdit={editingEnabled ? handleEditScan : undefined}
        />
      )}

      {formatTarget && (
        <FormatDialog
          summary={
            formatTarget === 'all'
              ? `All ${plural(flatScans.length, 'scan')}`
              : plural(selectedCount, 'selected scan')
          }
          hasEdits={downloadHasEdits}
          onSelect={startDownload}
          onClose={() => setFormatTarget(null)}
        />
      )}

      {confirmDialog}
    </main>
  );
}
